import * as React from "react";
import { useState, useEffect, useRef } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Box, Divider, Grid, IconButton, Stack } from "@mui/material";
import { ModifiedTextField } from "./textField";
import CloseIcon from "@mui/icons-material/Close";
import { DropzoneArea } from "../common/dropZone";
import { useStoreState, useStoreActions } from "easy-peasy";
import { findRTCurveValues } from "../../utils/xmlParser";
import { validateNewForm } from "./../../utils/graph-helper";

//  when we select new resin in recycled radio this component will trigger
export default function RecycledResinDialog({ open, setOpen }) {
  const [isUploadDisabled, setIsUploadDisabled] = useState(true);
  const nameRef = useRef("");

  let requirements = useStoreState((state) => state.requirements);
  let setRequirements = useStoreActions((actions) => actions.setRequirements);

  let recycledDialogErrors = useStoreState(
    (state) => state.recycledDialogErrors
  );

  let setRecycledDialogErrors = useStoreActions(
    (actions) => actions.setRecycledDialogErrors
  );

  let newFormErrors = useStoreState((state) => state.newFormErrors);
  const setNewFormErrors = useStoreActions(
    (actions) => actions.setNewFormErrors
  );

  useEffect(() => {
    if (
      fileName &&
      recycledDialogErrors?.thickness?.required == false &&
      recycledDialogErrors?.resinName?.required == false &&
      recycledDialogErrors?.recycledResinConc?.required == false
    ) {
      setIsUploadDisabled(false);
    } else {
      setIsUploadDisabled(true);
    }
  }, [open, isUploadDisabled]);

  const [resinName, setResinName] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [thickness, setThickness] = useState("");
  const [recycledResinConc, setRecycledResinConc] = useState("");
  // virginResinConc =100-recycledResinConc
  const [virginResinConc, setVirginResinConc] = useState("");
  const [rtCurve, setRTCurve] = useState({ rCurve: [], tCurve: [] });
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    if (
      requirements &&
      requirements.resinRequirement &&
      requirements.resinRequirement.newResinRecycled
    ) {
      let data = requirements.resinRequirement.newResinRecycled;
      setResinName(data.resinName);
      setBatchNumber(data.batchNumber);
      setThickness(data.thickness);
      setRTCurve(data.rtCurve);
      setFileName(data.fileName);
      if (data && data.dilutionDetails) {
        setRecycledResinConc(data.dilutionDetails.recycledResinConc);
        setVirginResinConc(data.dilutionDetails.virginResinConc);
      }
    }

    if (!open) {
      const errorRespMainForm = validateNewForm(recycledDialogErrors);
      if (errorRespMainForm) {
        setIsUploadDisabled(false);
      } else {
        setIsUploadDisabled(true);
      }
    }
  }, [
    requirements.selectedRecycledResin,
    requirements.resinRequirement.newResinRecycled,
  ]);

  //  button iside pop up when we click on this button everything on the pop up will be saved in state
  const handleUpload = () => {
    // Handle validation  here
    // Updating state on upload button click
    setRequirements({
      ...requirements,
      selectedRecycledResin: "",
      resinRequirement: {
        newResinvirgin: requirements.resinRequirement.newResinvirgin,
        newResinRecycled: {
          resinName,
          batchNumber,
          thickness,
          rtCurve,
          fileName: fileName,
          dilutionDetails: {
            recycledResinConc,
            virginResinConc,
          },
        },
      },
    });

    setOpen(false);
    setIsUploadDisabled(true);
  };

  //  close button to close the pop up
  const handleClose = () => {
    setOpen(false);
    setIsUploadDisabled(true);
  };

  const validateField = (name, value) => {
    let errorMessage = "";
    let stateObj = {};

    switch (name) {
      case "thickness": // Validation for the 'name' field
        if (value.trim() === "") {
          errorMessage = "Thickness is required.";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else if (value < 0.1 || value > 5) {
          errorMessage = "Thickness should be greater than 0.1 and less than 5";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else {
          errorMessage = "";
        }
        setThickness(parseFloat(value));
        setRecycledDialogErrors(() => {
          // This `prevErrors` is the latest sta  1te from the store
          return {
            ...recycledDialogErrors,
            ["thickness"]: stateObj,
          };
        });

        nameRef.thickness = value;

        /* Check both inputs are updated or not */
        break;

      case "upload":
        if (value.trim() === "") {
          errorMessage = "File is required.";
        } else {
          errorMessage = "";
        }
        break;
      case "recycledResinConc":
        // Validation for the 'password' field
        if (value.trim() === "") {
          errorMessage = "Recycled resin is required.";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else if (value < 0 || value > 100) {
          errorMessage = "Max 100 characters";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else {
          errorMessage = "";
          stateObj = {
            required: false,
            highlight: false,
          };
        }
        setRecycledResinConc(value);
        setVirginResinConc(
          value != "" ? 100 - parseFloat(value).toFixed(2) : 0
        );
        nameRef.recycledResinConc = value;
        setRecycledDialogErrors(() => {
          // This `prevErrors` is the latest sta  1te from the store
          return {
            ...recycledDialogErrors,
            ["recycledResinConc"]: stateObj,
          };
        });
        break;

      case "resinName":
        if (value.trim() === "") {
          errorMessage = "Resin name is required.";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else if (value.length > 55) {
          errorMessage = "Max 55 characters";
          stateObj = {
            required: true,
            highlight: true,
          };
        } else {
          errorMessage = "";
          stateObj = {
            required: false,
            highlight: false,
          };
        }
        setResinName(value);
        nameRef.resinName = value;
        setRecycledDialogErrors(() => {
          // This `prevErrors` is the latest sta  1te from the store
          return {
            ...recycledDialogErrors,
            ["resinName"]: stateObj,
          };
        });
      default:
        // No validation rule for this field
        break;
    }
    return errorMessage;
  };

  const inputChange = (event) => {
    let input = event.target.value;
    let name = event.target.name;
    console.log(name);
    // Prevent exceeding number of digits or decimal points
    const errorMessage = validateField(name, input);
    console.log(errorMessage);
    let obj = {};
    if (errorMessage == "") {
      obj = {
        required: false,
        highlight: false,
        error: "",
      };
    } else {
      obj = {
        required: true,
        highlight: true,
        error: errorMessage,
      };
    }

    setRecycledDialogErrors(() => {
      // This `prevErrors` is the latest state from the store
      return {
        ...recycledDialogErrors,
        [name]: obj,
      };
    });
    let errors = {
      ...recycledDialogErrors,
      [name]: obj,
    };
    console.log(errors);
    const errorRespMainForm = validateNewForm(errors);
    if (errorRespMainForm && fileName !== "") {
      setIsUploadDisabled(false);
      setNewFormErrors(() => {
        // This `prevErrors` is the latest state from the store
        return {
          ...newFormErrors,
          ["recycled"]: {
            required: false,
            highlight: false,
          },
        };
      });
    } else {
      setNewFormErrors(() => {
        // This `prevErrors` is the latest state from the store
        return {
          ...newFormErrors,
          ["recycled"]: {
            required: true,
            highlight: false,
          },
        };
      });
      setIsUploadDisabled(true);
    }
  };

  //  this called when we upload file in dropzone
  //  - read the cxf file then it will convert xml to JSON then we can read easily
  //  - read the reflectance and transmittance RT curve from the file
  //  -  before saving the RT curve into state it will pass from validation if validation pass will save in state otherwise error in console.log()
  // const handleUploadFile = (xmlJson, fileName, dialogNames) => {
  //   setFileName(fileName);
  //   let data = findRTCurveValues(xmlJson);
  //   if (data != -1) setRTCurve(data);
  //   else {
  //     setRTCurve({ rCurve: [], tCurve: [] });
  //     console.log("Invalid File Format");
  //     // disbale upload button
  //   }
  // };

  const handleUploadFile = async (xmlJson, fileName) => {
    setFileName(fileName);
    console.log(fileName);
    let data = findRTCurveValues(xmlJson);

    if (data != -1) {
      setRTCurve(data);
      if (
        fileName &&
        nameRef.thickness != "" &&
        nameRef.resinName != "" &&
        nameRef.recycledResinConc != ""
      ) {
        setIsUploadDisabled(false);
      } else {
        setIsUploadDisabled(true);
      }

      // call validate field option
    } else {
      setRTCurve({ rCurve: [], tCurve: [] });
      console.log("Invalid File Format");
      setIsUploadDisabled(true);
      // disbale upload button
    }
  };
  //  when we click on cross arrow button this function will trigger to reset the state
  const handleRemoveFile = () => {
    setIsUploadDisabled(true);
    setFileName("");
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={handleClose} fullWidth={true}>
        <DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            sx={{
              color: "#303C43",
              fontSize: 28,
              fontFamily: "Source Sans Pro",
              fontWeight: "600",
              lineHeight: "36px",
              wordWrap: "break-word",
              mb: 1,
            }}
          >
            New Recycled Resin
          </Box>
        </DialogTitle>
        <Divider sx={{ color: "#D9D9D9" }} />
        <DialogContent>
          <Box
            sx={{
              width: "95%",
              height: "100%",
              background: "white",
              borderRadius: "4px",
              border: "1px #C7C9C7 solid",
              padding: "12px",
              mb: 2,
              mt: 2,
            }}
          >
            <DropzoneArea
              handleUploadFile={handleUploadFile}
              fileName={fileName}
              handleRemoveFile={handleRemoveFile}
              noOfColorPoints={-1}
              dialogNames="recycledResin"
            />
            <Grid container sx={{ mt: 2 }}>
              <Grid xs={12}>
                <ModifiedTextField
                  heading="Resin Name"
                  name="resinName"
                  value={resinName}
                  //onChange={(e) => setResinName(e.target.value)}
                  onChange={(e) => inputChange(e)}
                  isError={recycledDialogErrors?.resinName?.highlight}
                  errorMessage={
                    recycledDialogErrors?.resinName?.highlight
                      ? recycledDialogErrors?.resinName?.error
                      : ""
                  }
                />
              </Grid>

              <Grid xs={12} sx={{ p: 1 }}></Grid>
              <Grid container xs={12}>
                <Grid xs={3}>
                  <ModifiedTextField
                    heading="Batch Number"
                    name="batchNumber"
                    value={batchNumber}
                    onChange={(e) => setBatchNumber(e.target.value)}
                  />
                </Grid>
                <Grid xs={1}></Grid>
                <Grid xs={3}>
                  <ModifiedTextField
                    heading="Thickness (mm)"
                    placeholder={"0.1-5"}
                    type="number"
                    name="thickness"
                    value={thickness}
                    // onChange={(e) =>
                    //   e.target.value >= 0 &&
                    //   e.target.value <= 5 &&
                    //   setThickness(e.target.value)
                    // }
                    onChange={(e) => inputChange(e)}
                    isError={recycledDialogErrors?.thickness?.highlight}
                    errorMessage={
                      recycledDialogErrors?.thickness?.highlight
                        ? "0.1 to 5"
                        : ""
                    }
                  />
                </Grid>
                <Grid xs={5}></Grid>
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              width: "95%",
              height: "100%",
              padding: "12px",
              background: "white",
              borderRadius: "4px",
              border: "1px #C7C9C7 solid",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              display: "inline-flex",
            }}
          >
            <Grid container>
              <Grid xs={12}>
                <Box
                  sx={{
                    color: "#303C43",
                    fontSize: "22px",
                    fontFamily: "Source Sans Pro",
                    fontWeight: 600,
                    lineHeight: "32px",
                    wordWrap: "break-word",
                    mb: 1,
                  }}
                >
                  Dilution Details
                </Box>
                <Divider />
              </Grid>

              <Grid container xs={12}>
                <Grid xs={5} sx={{ mt: 2 }}>
                  {" "}
                  <ModifiedTextField
                    heading="Recycled Resin Conc. (%)"
                    placeholder="0-100"
                    name="recycledResinConc"
                    type="number"
                    value={recycledResinConc}
                    // value={
                    //   requirements?.resinRequirement?.newResinRecycled
                    //     ?.dilutionDetails.recycledResinConc
                    // }
                    onChange={(e) => inputChange(e)}
                    isError={recycledDialogErrors?.recycledResinConc?.highlight}
                    errorMessage={
                      recycledDialogErrors?.recycledResinConc?.highlight
                        ? "0 to 100"
                        : ""
                    }
                  />
                </Grid>
                <Grid xs={1}></Grid>
                <Grid xs={4}>
                  {" "}
                  <Stack spacing={1} sx={{ mt: 2 }}>
                    <Box className="font-16 font-weight-600 line-height-24 font-family">
                      Virgin Resin Conc.
                    </Box>
                    <Box className="font-16 font-weight-400 line-height-24 font-family black-heading">
                      {virginResinConc}%
                    </Box>
                  </Stack>
                </Grid>
                <Grid xs={2}></Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: "flex-start",
            height: "80px",
            marginLeft: "3%",
          }}
        >
          <Button
            onClick={handleClose}
            sx={{
              mr: 1,
              width: "134px",
              height: "40px",
              padding: "8px 40px",
              borderRadius: "24px",
              border: "1px solid #385DAE",
              gap: "10px",
              color: "#385DAE",
              textTransform: "none",
              marginRight: "20px",
              fontFamily: "Source Sans Pro",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={isUploadDisabled}
            sx={{
              mr: 1,
              width: "134px",
              height: "40px",
              padding: "8px 40px",
              borderRadius: "24px",
              border: "1px solid #385DAE",
              gap: "10px",
              background: "linear-gradient(90deg, #3A5DAE 0%, #4EC3E0 100%)",
              border: "1px solid",
              color: "#FFFFFF",
              textTransform: "none",
              fontFamily: "Source Sans Pro",
            }}
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
